import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import '../GestionUsuarios.css'; // Reutilizaremos los estilos de GestionUsuarios

// Componente de Modal genérico para editar
const EditModal = ({ record, onClose, onSave, type }) => {
    const isCreating = !record;
    const [formData, setFormData] = useState(() => {
        if (isCreating) return { cedula: '', nombre: '', correo: '', telefonoOficina: '', telefonoCelular: '' };
        // Si estamos editando, usamos el `telefono` existente como `telefonoOficina`.
        return { ...record, telefonoOficina: record.telefonoOficina || record.telefono || '', telefonoCelular: record.telefonoCelular || '' };
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'telefonoOficina' || name === 'telefonoCelular') {
            const digitsOnly = value.replace(/\D/g, '');
            let formatted = digitsOnly;

            if (digitsOnly.length > 4) {
                // Inserta un guion después del cuarto dígito
                formatted = `${digitsOnly.substring(0, 4)}-${digitsOnly.substring(4, 8)}`;
            }
            setFormData(prev => ({ ...prev, [name]: formatted }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isCreating ? 'Crear' : 'Editar'} {type}</h2>
                    <button onClick={onClose} className="modal-close">×</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body">
                    <div className="form-group">
                        <label htmlFor="cedula">Cédula</label>
                        <input type="text" id="cedula" name="cedula" value={formData.cedula} onChange={handleChange} required disabled={!isCreating} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="nombre">Nombre</label>
                        <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="correo">Correo Electrónico</label>
                        <input type="email" id="correo" name="correo" value={formData.correo || ''} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <div style={{ flex: 1 }}>
                                <label htmlFor="telefonoOficina">Teléfono oficina</label>
                                <input type="tel" id="telefonoOficina" name="telefonoOficina" value={formData.telefonoOficina || ''} onChange={handleChange} placeholder="9999-8888" maxLength="9" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label htmlFor="telefonoCelular">Teléfono celular</label>
                                <input type="tel" id="telefonoCelular" name="telefonoCelular" value={formData.telefonoCelular || ''} onChange={handleChange} placeholder="9999-8888" maxLength="9" />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const GestionProveedores = () => {
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchProveedores = useCallback(async () => {
        setLoading(true);
        try {
            const proveedoresCollection = collection(db, 'SicTablaProveedores');
            const proveedoresSnapshot = await getDocs(proveedoresCollection);
            const proveedoresList = proveedoresSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            setProveedores(proveedoresList);
        } catch (error) {
            console.error("Error al obtener proveedores: ", error);
            alert("No se pudieron cargar los proveedores.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProveedores();
    }, [fetchProveedores]);

    const handleSave = async (recordData) => {
        try {
            const isCreating = !recordData.id;
            const docId = isCreating ? recordData.cedula : recordData.id;

            if (!docId) {
                alert("La cédula es obligatoria para crear un nuevo registro.");
                return;
            }

            const recordRef = doc(db, 'SicTablaProveedores', docId);

            if (isCreating) {
                await setDoc(recordRef, { ...recordData, fechacreacion: serverTimestamp() });
            } else {
                await updateDoc(recordRef, {
                    nombre: recordData.nombre,
                    correo: recordData.correo,
                    telefono: recordData.telefono,
                    telefonoCelular: recordData.telefonoCelular,
                    fechaModificacion: serverTimestamp()
                });
            }
            await fetchProveedores();
            closeModal();
        } catch (error) {
            console.error("Error al actualizar el registro: ", error);
            alert("Error al guardar los cambios.");
        }
    };

    const handleDelete = async (record) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar a "${record.nombre}"? Esta acción no se puede deshacer.`)) {
            return;
        }
        try {
            await deleteDoc(doc(db, 'SicTablaProveedores', record.id));
            await fetchProveedores(); // Recarga la lista de proveedores
        } catch (error) {
            console.error("Error al eliminar el proveedor: ", error);
            alert("Error al eliminar el proveedor.");
        }
    };

    const openModal = (record) => {
        setEditingRecord(record || null); // Pasa null si es para crear
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingRecord(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('es-CR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
        } catch (error) {
            return 'Fecha inválida';
        }
    };

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return 'N/A';
        // Eliminar todos los caracteres no numéricos
        const digitsOnly = String(phoneNumber).replace(/\D/g, ''); // Elimina todo lo que no sea dígito
        if (digitsOnly.length === 8) {
            return `${digitsOnly.substring(0, 4)}-${digitsOnly.substring(4, 8)}`;
        }
        return phoneNumber; // Devolver el original si no tiene 8 dígitos
    };

    const filteredProveedores = proveedores.filter(proveedor => {
        const term = searchTerm.toLowerCase();
        return (
            proveedor.nombre?.toLowerCase().includes(term) ||
            proveedor.cedula?.toLowerCase().includes(term) ||
            proveedor.correo?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return <div className="loading-container"><div className="loading-spinner"></div><p>Cargando proveedores...</p></div>;
    }

    return (
        <div className="gestion-usuarios-container">
            <div className="component-header">
                <h1>🚚 Gestión de Proveedores</h1>
                <div className="header-actions">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Buscar por nombre, cédula o correo..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn btn-primary" onClick={() => openModal(null)}>Crear Proveedor</button>
                </div>
            </div>

            <div className="card">
                <div className="table-container">
                    <table className="usuarios-table">
                        <thead>
                            <tr>
                                <th>Cédula</th>
                                <th>Nombre</th>
                                <th>Correo</th>
                                <th>Teléfono</th>
                                <th>Responsable</th>
                                <th>Fecha Creación</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProveedores.map(proveedor => (
                                <tr key={proveedor.id}>
                                    <td>{proveedor.cedula}</td>
                                    <td>{proveedor.nombre}</td>
                                    <td>{proveedor.correo || 'N/D'}</td>
                                    <td>{formatPhoneNumber(proveedor.telefonoOficina || proveedor.telefono)}</td>
                                    <td>{proveedor.responsable || 'N/D'}</td>
                                    <td>{formatDate(proveedor.fechacreacion)}</td>
                                    <td className="actions-cell">
                                        <div className="tooltip-container">
                                            <button className="icon-btn" onClick={() => openModal(proveedor)}>✏️</button>
                                            <span className="tooltip-text">Editar</span>
                                        </div>
                                        <div className="tooltip-container">
                                            <button className="icon-btn danger" onClick={() => handleDelete(proveedor)}>🗑️</button>
                                            <span className="tooltip-text">Eliminar</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <EditModal
                    record={editingRecord}
                    onClose={closeModal}
                    onSave={handleSave}
                    type="Proveedor"
                />
            )}
        </div>
    );
};

export default GestionProveedores;